/**
 * Zod validation schemas for API requests and domain types
 * Used by both frontend (client-side validation) and backend (server-side validation)
 */
import { z } from 'zod';
// Types are defined via Zod inference below, no need to import from types module
export * from './auth.js';
// ── Basic Type Enums ──────────────────────────────────────
export const SignalTypeSchema = z.enum([
    'audio',
    'midi',
    'sync',
    'cv_gate',
    'adat',
    'word_clock',
    'usb',
    'ethernet',
]);
export const ConnectorFormatSchema = z.enum([
    '1/4" TRS',
    '1/4" TS',
    '3.5mm TRS',
    '3.5mm TS',
    'XLR',
    '5-pin DIN',
    'optical',
    'BNC',
    'USB-C',
    'USB-B',
    'USB-A',
    'RCA',
    'RJ45',
    'combo XLR/TRS',
]);
export const DeviceCategorySchema = z.enum([
    'interface',
    'mixer',
    'synth',
    'drum_machine',
    'sampler',
    'controller',
    'effects',
    'monitoring',
    'midi_interface',
    'groovebox',
    'semi_modular',
    'computer',
    'usb_hub',
    'network_switch',
    'recorder',
    'microphone',
]);
export const ZoneEdgeSchema = z.enum([
    'top',
    'bottom',
    'left',
    'right',
]);
export const SubscriptionTierSchema = z.enum(['free', 'pro', 'studio']);
export const SubscriptionStatusSchema = z.enum([
    'active',
    'past_due',
    'canceled',
    'trialing',
]);
export const AIProviderSchema = z.enum([
    'bedrock',
    'anthropic',
    'openai',
    'openrouter',
]);
export const AISuggestionCategorySchema = z.enum([
    'monitoring',
    'audio-routing',
    'midi',
    'digital-expansion',
    'sync',
]);
// ── Port Schema ───────────────────────────────────────────
export const PortSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    type: z.enum(['input', 'output']),
    signalType: SignalTypeSchema,
    connectorFormat: ConnectorFormatSchema,
    color: z.string().regex(/^#[0-9A-F]{6}$/i),
    channels: z.number().int().positive().optional(),
    notes: z.string().optional(),
    exclusiveGroup: z.string().optional(),
});
export const CreatePortSchema = PortSchema.omit({ id: true, color: true });
// ── Device Schema ─────────────────────────────────────────
export const PositionSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export const DeviceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255),
    type: z.string().min(1).max(255),
    category: DeviceCategorySchema,
    manufacturer: z.string().optional(),
    gridSize: z.number().int().positive(),
    inputs: z.array(PortSchema),
    outputs: z.array(PortSchema),
    position: PositionSchema,
    zoneId: z.string().uuid().optional(),
    collapsed: z.boolean().default(false),
    portGroupStates: z.record(z.string(), z.boolean()).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateDeviceSchema = DeviceSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    inputs: z.array(CreatePortSchema),
    outputs: z.array(CreatePortSchema),
});
export const UpdateDeviceSchema = CreateDeviceSchema.partial();
// ── Connection Schema ─────────────────────────────────────
export const ConnectionSchema = z.object({
    id: z.string().uuid(),
    sourceDeviceId: z.string().uuid(),
    sourcePortId: z.string().uuid(),
    targetDeviceId: z.string().uuid(),
    targetPortId: z.string().uuid(),
    signalType: SignalTypeSchema.optional(),
    bundleId: z.string().uuid().optional(),
    aiSuggested: z.boolean().optional(),
    aiConfidence: z.number().min(0).max(1).optional(),
    createdAt: z.string().datetime(),
});
export const CreateConnectionSchema = ConnectionSchema.omit({
    id: true,
    createdAt: true,
});
// ── Zone Schema ───────────────────────────────────────────
export const ZoneConnectionPointSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    edge: ZoneEdgeSchema,
    signalType: SignalTypeSchema,
    internalDeviceId: z.string().uuid(),
    internalPortId: z.string().uuid(),
    position: z.number().min(0).max(1),
});
export const CreateZoneConnectionPointSchema = ZoneConnectionPointSchema.omit({
    id: true,
});
export const SizeSchema = z.object({
    width: z.number().positive(),
    height: z.number().positive(),
});
export const ZoneSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255),
    color: z.string().regex(/^#[0-9A-F]{6}$/i),
    position: PositionSchema,
    size: SizeSchema,
    collapsed: z.boolean().default(false),
    parentZoneId: z.string().uuid().optional(),
    deviceIds: z.array(z.string().uuid()),
    connectionPoints: z.array(ZoneConnectionPointSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateZoneSchema = ZoneSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    connectionPoints: z.array(CreateZoneConnectionPointSchema),
});
export const UpdateZoneSchema = CreateZoneSchema.partial();
// ── Layout Schema ─────────────────────────────────────────
export const ViewportSchema = z.object({
    x: z.number(),
    y: z.number(),
    zoom: z.number().positive(),
});
export const LayoutSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    thumbnail: z.string().optional(),
    isShared: z.boolean().default(false),
    teamId: z.string().optional(),
    viewport: ViewportSchema,
    layoutDirection: z.enum(['LR', 'TB']),
    version: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateLayoutSchema = LayoutSchema.omit({
    id: true,
    userId: true,
    version: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    viewport: ViewportSchema.optional(),
    layoutDirection: z.enum(['LR', 'TB']).default('LR'),
})
    .refine(data => data.name.length > 0, {
    message: 'Layout name is required',
    path: ['name'],
});
export const UpdateLayoutSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    thumbnail: z.string().optional(),
    isShared: z.boolean().optional(),
    teamId: z.string().uuid().optional(),
    viewport: ViewportSchema.optional(),
    layoutDirection: z.enum(['LR', 'TB']).optional(),
});
// ── User & Subscription Schema ────────────────────────────
export const UserPreferencesSchema = z.object({
    darkMode: z.boolean().default(false),
    defaultLayoutDirection: z.enum(['LR', 'TB']).default('LR'),
    aiProvider: AIProviderSchema.default('bedrock'),
    autoSuggest: z.boolean().default(true),
});
export const UserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().min(1),
    avatarUrl: z.string().url().optional(),
    preferences: UserPreferencesSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const SubscriptionFeaturesSchema = z.object({
    maxLayouts: z.number().int().positive(),
    maxDevicesPerLayout: z.number().int().positive(),
    maxZonesPerLayout: z.number().int().positive(),
    aiSuggestions: z.boolean(),
    cloudSync: z.boolean(),
    teamSharing: z.boolean(),
    exportFormats: z.array(z.string()),
    // Bare `z.number()` (not `.int().positive()`) so `Number.POSITIVE_INFINITY`
    // passes validation for uncapped tiers — same treatment as `maxActiveShares`.
    maxMidiRoutes: z.number(),
    maxSequencerProjects: z.number(),
    sequencerKits: z.enum(['basic', 'all']),
    sequencerSave: z.boolean(),
    maxSequencerMidiPads: z.number(),
    sampleSlicer: z.boolean(),
});
export const SubscriptionSchema = z.object({
    tier: SubscriptionTierSchema,
    status: SubscriptionStatusSchema,
    currentPeriodEnd: z.string().datetime().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
    features: SubscriptionFeaturesSchema,
});
// ── AI Schema ─────────────────────────────────────────────
export const AISuggestionSchema = z.object({
    id: z.string().uuid(),
    sourceDeviceId: z.string().uuid(),
    sourcePortId: z.string().uuid(),
    targetDeviceId: z.string().uuid(),
    targetPortId: z.string().uuid(),
    signalType: SignalTypeSchema,
    confidence: z.number().min(0).max(1),
    reason: z.string().min(1),
    category: AISuggestionCategorySchema,
    priority: z.number().int().nonnegative(),
});
export const AISuggestRequestSchema = z.object({
    layoutId: z.string().uuid(),
    focusZoneId: z.string().uuid().optional(),
    userPreferences: UserPreferencesSchema.partial().optional(),
    maxSuggestions: z.number().int().positive().default(10),
});
export const AISuggestResponseSchema = z.object({
    suggestions: z.array(AISuggestionSchema),
    appliedRules: z.array(z.string()),
    generatedAt: z.string().datetime(),
});
// ── Batch Write Schema ────────────────────────────────────
export const BatchActionSchema = z.union([
    z.object({
        action: z.literal('createDevice'),
        device: CreateDeviceSchema,
    }),
    z.object({
        action: z.literal('updateDevice'),
        deviceId: z.string().uuid(),
        updates: UpdateDeviceSchema,
    }),
    z.object({
        action: z.literal('deleteDevice'),
        deviceId: z.string().uuid(),
    }),
    z.object({
        action: z.literal('createConnection'),
        connection: CreateConnectionSchema,
    }),
    z.object({
        action: z.literal('deleteConnection'),
        connectionId: z.string().uuid(),
    }),
    z.object({
        action: z.literal('createZone'),
        zone: CreateZoneSchema,
    }),
    z.object({
        action: z.literal('updateZone'),
        zoneId: z.string().uuid(),
        updates: UpdateZoneSchema,
    }),
    z.object({
        action: z.literal('deleteZone'),
        zoneId: z.string().uuid(),
    }),
]);
export const BatchWriteRequestSchema = z.object({
    operations: z.array(BatchActionSchema).min(1).max(100),
    layoutVersion: z.number().int().nonnegative(),
});
export const BatchWriteResponseSchema = z.object({
    success: z.boolean(),
    newVersion: z.number().int().nonnegative(),
    errors: z
        .array(z.object({
        index: z.number().int().nonnegative(),
        message: z.string(),
    }))
        .optional(),
});
// ── Full Layout Response Schema ───────────────────────────
export const FullLayoutResponseSchema = z.object({
    layout: LayoutSchema,
    devices: z.array(DeviceSchema),
    connections: z.array(ConnectionSchema),
    zones: z.array(ZoneSchema),
});
//# sourceMappingURL=index.js.map